// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import styles from '@app/features/channel/components/embeds/ChannelEmbed.module.css';
import type {LinkComponentProps} from '@app/features/channel/components/embeds/channel_embed/ChannelEmbedShared';
import {SUPPRESS_EMBEDS_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {isRailWindow} from '@app/features/konvo_rail/utils/RailWindow';
import {openClickUpTaskLink} from '@app/features/konvo_tasks/utils/ClickUpTaskLink';
import TeamsMeetings from '@app/features/konvo_teams_meetings/state/TeamsMeetings';
import {isTeamsMeetingLink} from '@app/features/konvo_teams_meetings/utils/TeamsMeetingLinks';
import * as MessageCommands from '@app/features/messaging/commands/MessageCommands';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {openExternalLink} from '@app/features/messaging/utils/ExternalLinkUtils';
import {isInternalChannelHost} from '@app/features/navigation/utils/DeepLinkUtils';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import type {FC} from 'react';

/** A link back into this Konvo (same host as the app) that a plain click should route in place. */
function internalPathFor(url: string): string | null {
	try {
		const parsed = new URL(url, window.location.origin);
		if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
		if (!isInternalChannelHost(parsed.host)) return null;
		return parsed.pathname + parsed.search + parsed.hash;
	} catch {
		return null;
	}
}

export const EmbedLink: FC<LinkComponentProps> = observer(({url, children, className}) => {
	const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.stopPropagation();
		// KONVO: "Join in Konvo" and friends point at this very app. Inside the PWA or the
		// desktop shell a new tab is the wrong place for them, so a plain left click navigates
		// in place; modifier clicks keep the browser's open-in-new-tab behaviour.
		const plainClick = e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
		const internalPath = plainClick ? internalPathFor(url) : null;
		if (internalPath) {
			e.preventDefault();
			// The Rail never moves its own router: the main window opens the route.
			if (isRailWindow()) {
				void import('@app/features/konvo_rail/RailHost').then((module) => module.openInMainWindow(internalPath));
				return;
			}
			RouterUtils.transitionTo(internalPath);
			return;
		}
		e.preventDefault();
		// A Teams meeting opens inside Konvo for people it is on for (TeamsMeetings falls back to
		// Teams otherwise); the Rail hands it to Teams, a meeting never runs in the Rail.
		if (plainClick && !isRailWindow() && isTeamsMeetingLink(url)) {
			void TeamsMeetings.open(url, null, openExternalLink);
			return;
		}
		// A ClickUp task opens as Konvo's task view; modifier clicks still go to ClickUp.
		if (plainClick && openClickUpTaskLink(url)) return;
		openExternalLink(url);
	};
	return (
		<FocusRing data-flx="channel.embeds.embed.link-component.focus-ring">
			<a
				className={clsx(styles.embedLink, className)}
				href={url}
				rel="noopener noreferrer"
				target="_blank"
				onClick={handleClick}
				data-flx="channel.embeds.embed.link-component.a"
			>
				{children}
			</a>
		</FocusRing>
	);
});
export const SuppressEmbedsConfirmModal: FC<{message: Message}> = ({message}) => {
	const {i18n} = useLingui();
	return (
		<ConfirmModal
			title={i18n._(SUPPRESS_EMBEDS_DESCRIPTOR)}
			description={
				<Trans>
					Are you sure you want to suppress all link embeds on this message? This action will hide all embeds from this
					message.
				</Trans>
			}
			primaryText={i18n._(SUPPRESS_EMBEDS_DESCRIPTOR)}
			primaryVariant="danger"
			onPrimary={async () => {
				await MessageCommands.toggleSuppressEmbeds(message.channelId, message.id, message.flags);
			}}
			data-flx="channel.embeds.embed.suppress-embeds-confirm-modal.confirm-modal"
		/>
	);
};
